import createReducer from '../../../../../../utils/createReducer';
import { ACTION_PREFIX } from '../../../sportsbook-constants';
import type { AppDispatch, RootState, ThunkAction } from '~/store/types';
import selectSportsbookState from '../../../sportsbook-state-selector';
import { fetchAcceptMode as apiFetchAcceptMode, updateAcceptMode } from '../../../api/betting-service-api';
import { AcceptMode, BetslipPendingItem, BetslipTab } from '../sportsbook-betslip-types';
import { selectIsAuthenticated } from '../../../sportsbook-root-selectors';
import { fetchBalance } from '../../../../../account/ducks';
import { AnyAction } from 'redux';
import reportSportsbookError from '../../../utils/sportsbook-error-reporter';
import { debounce } from '@comeon/utility';
import { authenticationSelector } from '../../../../../authentication/ducks/selectors';

// State
type State = {
	tab: BetslipTab;
	isExpanded: boolean;
	isSettingsOpen: boolean;
	isShareOpen: boolean;
	isKeepSelections: boolean;
	shouldApplyBonus: boolean;
	acceptMode: AcceptMode;
	pendingItems: Array<BetslipPendingItem>;
};

const initialState: State = {
	tab: BetslipTab.SINGLES,
	isExpanded: false,
	isSettingsOpen: false,
	isShareOpen: false,
	isKeepSelections: false,
	shouldApplyBonus: false,
	acceptMode: AcceptMode.ALWAYS_ASK,
	pendingItems: []
};

// Selectors
const selectState = (state: RootState) => selectSportsbookState(state).betslip.common;
export const selectTab = (state: RootState) => selectState(state).tab;
export const selectIsExpanded = (state: RootState) => selectState(state).isExpanded;
export const selectIsSettingsOpen = (state: RootState) => selectState(state).isSettingsOpen;
export const selectIsShareOpen = (state: RootState) => selectState(state).isShareOpen;
export const selectAcceptMode = (state: RootState) => selectState(state).acceptMode;
export const selectKeepSelections = (state: RootState) => selectState(state).isKeepSelections;
export const selectShouldApplyBonus = (state: RootState) => selectState(state).shouldApplyBonus;
export const selectPendingItems = (state: RootState) => selectState(state).pendingItems;

// Actions
const TAB_CHANGED = `${ACTION_PREFIX}BETSLIP/COMMON/TAB_CHANGED`;
type TabChangedAction = {
	type: typeof TAB_CHANGED;
	payload: { tab: BetslipTab };
};

const EXPANDED = `${ACTION_PREFIX}BETSLIP/COMMON/EXPANDED`;
type ExpandedAction = {
	type: typeof EXPANDED;
};
const MINIMIZED = `${ACTION_PREFIX}BETSLIP/COMMON/MINIMIZED`;
type MinimizedAction = {
	type: typeof MINIMIZED;
};

const SETTINGS_OPENED = `${ACTION_PREFIX}BETSLIP/COMMON/SETTINGS_OPENED`;
type SettingsOpenedAction = {
	type: typeof SETTINGS_OPENED;
};
const SETTINGS_CLOSED = `${ACTION_PREFIX}BETSLIP/COMMON/SETTINGS_CLOSED`;
type SettingsClosedAction = {
	type: typeof SETTINGS_CLOSED;
};

const SHARE_OPENED = `${ACTION_PREFIX}BETSLIP/COMMON/SHARE_OPENED`;
type ShareOpenedAction = {
	type: typeof SHARE_OPENED;
};
const SHARE_CLOSED = `${ACTION_PREFIX}BETSLIP/COMMON/SHARE_CLOSED`;
type ShareClosedAction = {
	type: typeof SHARE_CLOSED;
};

const KEEP_SELECTIONS_CHANGED = `${ACTION_PREFIX}BETSLIP/COMMON/KEEP_SELECTIONS_CHANGED`;
type KeepSelectionsChangedAction = {
	type: typeof KEEP_SELECTIONS_CHANGED;
	payload: {
		isKeepSelections: boolean;
	};
};

const SHOULD_APPLY_BONUS_CHANGED = `${ACTION_PREFIX}BETSLIP/COMMON/SHOULD_APPLY_BONUS_CHANGED`;
type ShouldApplyBonusChangedAction = {
	type: typeof SHOULD_APPLY_BONUS_CHANGED;
	payload: {
		shouldApplyBonus: boolean;
	};
};

const PENDING_ITEMS_ADDED = `${ACTION_PREFIX}BETSLIP/COMMON/PENDING_ITEMS_ADDED`;
type PendingItemssAddedAction = {
	type: typeof PENDING_ITEMS_ADDED;
	payload: {
		pendingItems: BetslipPendingItem;
	};
};

const PENDING_ITEMS_REMOVED = `${ACTION_PREFIX}BETSLIP/COMMON/PENDING_ITEMS_REMOVED`;
type PendingItemsRemovedAction = {
	type: typeof PENDING_ITEMS_REMOVED;
	payload: {
		selectionIds: Array<string>;
	};
};

const ACCEPT_MODE_UPDATED = `${ACTION_PREFIX}BETSLIP/COMMON/ACCEPT_MODE_UPDATED`;
type AcceptModeUpdatedAction = {
	type: typeof ACCEPT_MODE_UPDATED;
	payload: {
		acceptMode: AcceptMode;
	};
};

const DISPOSED = `${ACTION_PREFIX}BETSLIP/COMMON/DISPOSED`;
type DisposedAction = {
	type: typeof DISPOSED;
};

// ActionCreators
export const changeTab = (tab: BetslipTab): TabChangedAction => ({
	type: TAB_CHANGED,
	payload: { tab }
});

const reloadBalanceDebounced = debounce(
	(dispatch: AppDispatch, isAuthenticated: () => boolean) => {
		if (!isAuthenticated()) {
			return;
		}

		try {
			dispatch(fetchBalance() as unknown as AnyAction);
		} catch (error) {
			reportSportsbookError('Failed to reload balance', error);
		}
	},
	5000,
	true
);

// Automatic balance updates are flaky, so we manually update the balance in some scenarios
export const reloadBalance = (): ThunkAction => (dispatch, getState) => {
	reloadBalanceDebounced(dispatch, () => authenticationSelector(getState()));
};

export const expand = (): ThunkAction<ExpandedAction> => dispatch => {
	dispatch({
		type: EXPANDED
	});
	// Automatic balance updates are flaky, so manually update the balance when betslip expands
	dispatch(reloadBalance());
};

export const minimize = (): MinimizedAction => ({
	type: MINIMIZED
});

export const openSettings = (): SettingsOpenedAction => ({
	type: SETTINGS_OPENED
});
export const closeSettings = (): SettingsClosedAction => ({
	type: SETTINGS_CLOSED
});
export const openShare = (): ShareOpenedAction => ({ type: SHARE_OPENED });
export const closeShare = (): ShareClosedAction => ({ type: SHARE_CLOSED });

export const changeKeepSelections = (newValue: boolean): KeepSelectionsChangedAction => ({
	type: KEEP_SELECTIONS_CHANGED,
	payload: {
		isKeepSelections: newValue
	}
});

export const addPendingItems = (newItem: BetslipPendingItem): PendingItemssAddedAction => ({
	type: PENDING_ITEMS_ADDED,
	payload: {
		pendingItems: newItem
	}
});
export const removePendingItems = (selectionIds: Array<string>): PendingItemsRemovedAction => ({
	type: PENDING_ITEMS_REMOVED,
	payload: { selectionIds }
});

export const changeShouldApplyBonus = (newValue: boolean): ShouldApplyBonusChangedAction => ({
	type: SHOULD_APPLY_BONUS_CHANGED,
	payload: {
		shouldApplyBonus: newValue
	}
});

export const changeAcceptMode =
	(newValue: AcceptMode): ThunkAction<AcceptModeUpdatedAction> =>
	async (dispatch, getState) => {
		const oldValue = selectAcceptMode(getState());
		dispatch({
			type: ACCEPT_MODE_UPDATED,
			payload: {
				acceptMode: newValue
			}
		});
		try {
			await updateAcceptMode({ acceptMode: newValue });
		} catch {
			dispatch({
				type: ACCEPT_MODE_UPDATED,
				payload: {
					acceptMode: oldValue
				}
			});
		}
	};

const refreshAcceptMode = (): ThunkAction<AcceptModeUpdatedAction> => async (dispatch, getState) => {
	const isAuthenticated = selectIsAuthenticated(getState());
	if (isAuthenticated) {
		const { acceptMode } = await apiFetchAcceptMode();
		dispatch({ type: ACCEPT_MODE_UPDATED, payload: { acceptMode } });
		return;
	}
	dispatch({ type: ACCEPT_MODE_UPDATED, payload: { acceptMode: AcceptMode.ACCEPT_ALL_CHANGES } });
};

export const inititalize = refreshAcceptMode;

export const dispose = (): DisposedAction => ({ type: DISPOSED });

// Reducer
const handleTabChanged = (state: State, { payload: { tab } }: TabChangedAction) => {
	return {
		...state,
		tab
	};
};

const handleExpanded = (state: State) => ({
	...state,
	isExpanded: true
});

const handleMinimized = (state: State) => ({
	...state,
	isExpanded: false,
	isSettingsOpen: false,
	shareOpened: false
});

const handleSettingsOpened = (state: State) => ({
	...state,
	isSettingsOpen: true
});
const handleSettingsClosed = (state: State) => ({
	...state,
	isSettingsOpen: false
});

const handleShareOpened = (state: State) => ({
	...state,
	isShareOpen: true
});
const handleShareClosed = (state: State) => ({
	...state,
	isShareOpen: false
});

const handleKeepSelectionsChanged = (state: State, { payload }: KeepSelectionsChangedAction) =>
	payload.isKeepSelections !== state.isKeepSelections
		? { ...state, isKeepSelections: payload.isKeepSelections }
		: state;

const handleShouldApplyBonusChanged = (state: State, { payload }: ShouldApplyBonusChangedAction) =>
	payload.shouldApplyBonus !== state.shouldApplyBonus
		? { ...state, shouldApplyBonus: payload.shouldApplyBonus }
		: state;

const handlePendingItemsAdded = (state: State, { payload }: PendingItemssAddedAction) => ({
	...state,
	pendingItems: [...state.pendingItems, payload.pendingItems]
});

const handlePendingItemsRemoved = (state: State, { payload: { selectionIds } }: PendingItemsRemovedAction): State => ({
	...state,
	pendingItems: [
		...state.pendingItems.filter(
			existingItem => !selectionIds.some(selectionId => selectionId === existingItem.selection.id)
		)
	]
});

const handleAcceptModeChanged = (state: State, { payload }: AcceptModeUpdatedAction) =>
	payload.acceptMode !== state.acceptMode ? { ...state, acceptMode: payload.acceptMode } : state;

const handleDisposed = (state: State) => ({
	...initialState,
	isKeepSelections: state.isKeepSelections,
	acceptMode: state.acceptMode
});

const reducer = createReducer<State>(initialState, {
	[TAB_CHANGED]: handleTabChanged,
	[EXPANDED]: handleExpanded,
	[MINIMIZED]: handleMinimized,
	[SETTINGS_OPENED]: handleSettingsOpened,
	[SETTINGS_CLOSED]: handleSettingsClosed,
	[SHARE_OPENED]: handleShareOpened,
	[SHARE_CLOSED]: handleShareClosed,
	[KEEP_SELECTIONS_CHANGED]: handleKeepSelectionsChanged,
	[SHOULD_APPLY_BONUS_CHANGED]: handleShouldApplyBonusChanged,
	[PENDING_ITEMS_ADDED]: handlePendingItemsAdded,
	[PENDING_ITEMS_REMOVED]: handlePendingItemsRemoved,
	[ACCEPT_MODE_UPDATED]: handleAcceptModeChanged,
	[DISPOSED]: handleDisposed
});

export default reducer;
